import { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useTranslation } from 'react-i18next';
import { StripeCustomer } from 'src/modules/member/models/member';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { Box } from '@mui/system';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { getAllMemberStripeCustomer } from 'src/shared/services/stripeCustomerService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { MemberStripeCustomerSearchFilter } from '../models/memberStripeCustomerSearchFilter';

type MemberStripeCustomerDataGridProps = {
  searchFilter: MemberStripeCustomerSearchFilter;
  searchFilterCallback: (searchFilter: MemberStripeCustomerSearchFilter) => void;
};

export const MemberStripeCustomerDataGrid = (props: MemberStripeCustomerDataGridProps) => {
  const { searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<StripeCustomer[]>([]);

  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = <K extends keyof DataGridModel>(k: K, v: DataGridModel[K]) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAllMemberStripeCustomer(searchFilter);
      const memberStripeCustomerResult = extendObjectWithId(result.data.results, 'firstName');
      updateData('totalRows', result.data.totalItems);

      setTimeout(() => {
        setRows(memberStripeCustomerResult);
      }, 100);
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onRowClick = () => {
    ConsoleHelper.log();
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: t('invoice:stripeStatus.tableHeader.firstName'),
      sortable: true,
      flex: 1.2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.firstName}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('invoice:stripeStatus.tableHeader.lastName'),
      sortable: true,
      flex: 1.2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName}</>;
      },
    },

    {
      field: 'cardNumber',
      headerName: t('invoice:stripeStatus.tableHeader.cardNumber'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.cardNumber}</>;
      },
    },
    {
      field: 'validThru',
      headerName: t('invoice:stripeStatus.tableHeader.validThru'),
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      type: 'number',
      flex: 1.2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.validThru}</>;
      },
    },
    {
      field: 'createdAt',
      headerName: t('invoice:stripeStatus.tableHeader.createdAt'),
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      type: 'number',
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt)} </>;
      },
    },
    {
      field: 'stripe_id',
      headerName: t('invoice:stripeStatus.tableHeader.stripeId'),
      headerAlign: 'left',
      align: 'left',
      sortable: true,
      type: 'number',
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.stripe_id}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<StripeCustomer>
        autoHeight
        columns={columns}
        rows={rows}
        disablePointer
        disableSelectionOnClick
        onRowClick={onRowClick}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        selectionModel={[]}
        sortModel={[
          {
            field: searchFilter.sortCol,
            sort: searchFilter.sortDir,
          },
        ]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length > 0) {
            const { field } = newSortModel[0];
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: field,
              sortDir: newSortModel[0].sort,
            });
          } else {
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: searchFilter.sortCol,
              sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
            });
          }
        }}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
